
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { processStripePayment } from '@/utils/stripeService';
import { processBitcoinPayment } from '@/utils/bitcoinService';

export const processTransfer = async (
  email: string, 
  pin: string, 
  amount: number,
  isPinExists: boolean,
  paymentMethod: string = 'stripe'
): Promise<boolean> => {
  try {
    // First verify if the PIN is valid for this email
    const { data, error } = await supabase
      .from('email_pins')
      .select('pin')
      .eq('email', email)
      .maybeSingle();
    
    const isPinValid = data && data.pin === pin;
    
    if (!isPinValid && isPinExists) {
      toast.error('Invalid PIN. Please try again.');
      return false;
    }
    
    // For new users, we'll save their PIN
    if (!isPinExists) {
      const { error: saveError } = await supabase
        .from('email_pins')
        .upsert({
          email,
          pin,
          updated_at: new Date().toISOString()
        });
      
      if (saveError) {
        toast.error('Failed to save PIN');
        return false;
      }
    }
    
    // Try to fetch recipient data from Supabase
    const { data: recipientData } = await supabase
      .from('profiles')
      .select('id, username')
      .eq('email', email)
      .maybeSingle();
    
    const recipientId = recipientData?.id || `email:${email}`;
    
    // Process payment based on payment method
    let paymentResult;
    
    if (paymentMethod === 'bitcoin') {
      // Process Bitcoin payment
      toast.info('Processing Bitcoin payment...');
      paymentResult = await processBitcoinPayment(amount, email);
    } else {
      // Default to Stripe payment with mock data
      const mockCardData = {
        cardNumber: '4242424242424242',
        cardholderName: 'Demo User',
        expiryDate: '12/25',
        cvv: '123',
        saveCard: true
      };
      
      paymentResult = await processStripePayment(mockCardData, recipientId);
    }
    
    if (!paymentResult.success) {
      toast.error(paymentResult.error || 'Payment failed');
      return false;
    }
    
    // Save the transaction in Supabase
    const { data: userData } = await supabase.auth.getUser();
    if (userData?.user) {
      await supabase
        .from('transactions')
        .insert({
          amount,
          method: paymentMethod === 'bitcoin' ? 'bitcoin_transfer' : 'pin_email_transfer',
          user_id: userData.user.id,
          recipient_id: recipientId,
          status: 'completed',
          transaction_id: paymentResult.transactionId,
          metadata: {
            pin_verified: true,
            recipient_email: email,
            payment_method: paymentMethod
          }
        });
    }
    
    return true;
  } catch (error: any) {
    console.error('Error processing transfer:', error);
    toast.error('An unexpected error occurred: ' + error.message);
    return false;
  }
};
